import React, { useEffect, useRef, useState } from 'react';
import { Box, TextField, InputAdornment, Autocomplete, CircularProgress } from '@mui/material';
import { FontIcon } from './FontIcon';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import type { Geopoint as Interface } from '../../design/components';
import { parseGeopoint, formatGeopoint, initLeafletIcon, DEFAULT_CENTER } from '../shared/geopoint';

initLeafletIcon();

async function reverseGeocode(lat: number, lng: number): Promise<string | null> {
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`,
      { headers: { 'User-Agent': 'mksys' } }
    );
    if (!response.ok) return null;
    const data = await response.json();
    return data.display_name || null;
  } catch {
    return null;
  }
}

interface AddressSuggestion {
  label: string;
  lat: number;
  lng: number;
}

async function searchAddresses(query: string): Promise<AddressSuggestion[]> {
  if (!query || query.length < 3) return [];
  try {
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=5`,
      { headers: { 'User-Agent': 'mksys' } }
    );
    if (!response.ok) return [];
    const data = await response.json();
    return data.map((item: { display_name: string; lat: string; lon: string }) => ({
      label: item.display_name,
      lat: parseFloat(item.lat),
      lng: parseFloat(item.lon),
    }));
  } catch {
    return [];
  }
}

function MapClickHandler({ onLocationChange }: { onLocationChange: (lat: number, lng: number) => void }) {
  useMapEvents({
    click(e) {
      onLocationChange(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

function MapViewUpdater({ center }: { center: [number, number] }) {
  const map = useMap();
  const prevCenter = useRef<[number, number] | null>(null);

  useEffect(() => {
    if (prevCenter.current && (prevCenter.current[0] !== center[0] || prevCenter.current[1] !== center[1])) {
      map.setView(center);
    }
    prevCenter.current = center;
  }, [center, map]);

  return null;
}

export const Geopoint: React.FC<Interface> = ({
  name,
  description,
  state,
  hidden,
  readonly,
  disabled,
  required,
  error,
}) => {
  const [value, setValue] = state || ['', () => {}];
  const [addressInput, setAddressInput] = useState('');
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  const position = parseGeopoint(value) || DEFAULT_CENTER;
  const skipReverseGeocode = useRef(false);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reverse geocode: coordinates → address
  useEffect(() => {
    if (skipReverseGeocode.current) {
      skipReverseGeocode.current = false;
      return;
    }
    const coords = parseGeopoint(value);
    if (coords) {
      reverseGeocode(coords[0], coords[1]).then((addr) => {
        if (addr) setAddressInput(addr);
      });
    }
  }, [value]);

  const handleLocationChange = (lat: number, lng: number) => {
    if (readonly || disabled) return;
    setValue(formatGeopoint(lat, lng));
  };

  const handleCoordinateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
  };

  const handleAddressInputChange = (_event: React.SyntheticEvent, newInputValue: string) => {
    setAddressInput(newInputValue);

    // Debounce search
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(async () => {
      if (newInputValue.length >= 3) {
        setLoading(true);
        const results = await searchAddresses(newInputValue);
        setSuggestions(results);
        setLoading(false);
      } else {
        setSuggestions([]);
      }
    }, 300);
  };

  const handleSuggestionSelect = (_event: React.SyntheticEvent, value: string | AddressSuggestion | null) => {
    if (value && typeof value !== 'string') {
      skipReverseGeocode.current = true;
      setAddressInput(value.label);
      setValue(formatGeopoint(value.lat, value.lng));
      setSuggestions([]);
    }
  };

  return (
    <Box sx={{ display: hidden ? 'none' : 'flex', flexDirection: 'column', mb: 2 }}>
      <TextField
        id={name}
        name={name}
        label={name}
        value={value}
        onChange={handleCoordinateChange}
        helperText={error || description}
        error={!!error}
        disabled={disabled}
        required={required}
        fullWidth
        slotProps={{
          input: {
            readOnly: readonly,
            startAdornment: (
              <InputAdornment position="start">
                <FontIcon name="LocationOn" color={error ? '#d32f2f' : 'rgba(0,0,0,0.54)'} />
              </InputAdornment>
            ),
          },
        }}
        placeholder="lat,lng (e.g., 40.7128,-74.0060)"
        sx={{ '& .MuiOutlinedInput-root': { borderBottomLeftRadius: 0, borderBottomRightRadius: 0 } }}
      />
      <Autocomplete
        freeSolo
        options={suggestions}
        getOptionKey={(option) => typeof option === 'string' ? option : `${option.lat},${option.lng}`}
        getOptionLabel={(option) => typeof option === 'string' ? option : option.label}
        inputValue={addressInput}
        onInputChange={handleAddressInputChange}
        onChange={handleSuggestionSelect}
        loading={loading}
        disabled={disabled}
        readOnly={readonly}
        filterOptions={(x) => x}
        sx={{ '& .MuiOutlinedInput-root': { borderTopLeftRadius: 0, borderTopRightRadius: 0 }, mt: '-1px' }}
        renderInput={(params) => (
          <TextField
            {...params}
            id={`${name}-address`}
            placeholder="Search address..."
            InputProps={(() => {
              const { ref: _ref, ...rest } = params.InputProps;
              return {
                ...rest,
                startAdornment: (
                  <>
                    <InputAdornment position="start">
                      <FontIcon name="Search" color="rgba(0,0,0,0.54)" />
                    </InputAdornment>
                    {params.InputProps.startAdornment}
                  </>
                ),
                endAdornment: (
                  <>
                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              };
            })()}
          />
        )}
      />
      <Box
        sx={{
          height: 300,
          borderRadius: 1,
          overflow: 'hidden',
          border: 1,
          borderColor: error ? 'error.main' : 'divider',
          opacity: disabled ? 0.5 : 1,
          pointerEvents: disabled || readonly ? 'none' : 'auto',
          mt: 1,
        }}
      >
        <MapContainer
          center={position}
          zoom={13}
          style={{ height: '100%', width: '100%' }}
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <Marker
            position={position}
            draggable={!readonly && !disabled}
            eventHandlers={{
              dragend: (e) => {
                const marker = e.target;
                const pos = marker.getLatLng();
                handleLocationChange(pos.lat, pos.lng);
              },
            }}
          />
          <MapClickHandler onLocationChange={handleLocationChange} />
          <MapViewUpdater center={position} />
        </MapContainer>
      </Box>
    </Box>
  );
};
